const { LOCALES, UI } = require("./locales");
const guide = require("./guideArticle");
const forecast = require("./forecastArticle");
const predict = require("./predictArticle");
const history = require("./historyArticle");
const models = require("./modelsArticle");
const dashboard = require("./dashboardArticle");

const ORIGIN = "https://aviatortrends.com";
const ARTICLES = [guide, forecast, predict, history, models, dashboard];

const ARTICLE_CSS = `
.at-page{max-width:40rem;margin:0 auto;padding:0.75rem 1rem 2.5rem;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;line-height:1.6;color:#101418}
.at-brand{display:flex;align-items:center;gap:0.6rem;padding:0.7rem 0 1rem;text-decoration:none;color:inherit;font-weight:800;letter-spacing:.04em}
.at-brand span{background:linear-gradient(90deg,rgb(52,180,255),rgb(145,62,248),rgb(192,23,180));-webkit-background-clip:text;background-clip:text;color:transparent}
.at-langs{display:flex;flex-wrap:wrap;gap:0.4rem;margin:0 0 1.1rem}
.at-langs a{display:inline-flex;align-items:center;min-height:40px;padding:0.35rem 0.75rem;border-radius:999px;border:1px solid rgba(52,180,255,.45);text-decoration:none;color:inherit;font-size:.85rem;background:rgba(52,180,255,.08)}
.at-kicker{margin:0 0 .4rem;font-size:.75rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:rgb(52,180,255)}
.at-page h1{font-size:1.7rem;line-height:1.15;margin:0 0 1rem}
.at-page h2{font-size:1.2rem;margin:1.6rem 0 .6rem}
.at-page p,.at-page li{font-size:1.05rem}
.at-page figure{margin:1rem 0 1.25rem}
.at-page img{width:100%;height:auto;border-radius:16px;display:block;border:1px solid rgba(145,62,248,.25)}
.at-page figcaption{margin-top:.45rem;font-size:.88rem;color:#4a5568}
.at-callout{margin:1.1rem 0;padding:1rem;border-radius:16px;border-left:4px solid rgb(52,180,255);background:rgba(52,180,255,.08)}
.at-callout.warn{border-left-color:rgb(192,23,180);background:rgba(192,23,180,.08)}
.at-callout.safe{border-left-color:rgb(145,62,248);background:rgba(145,62,248,.08)}
.at-callout h2{margin-top:0}
.at-note{font-weight:700;border-left:4px solid rgb(52,180,255);padding-left:.75rem}
.at-cta{display:flex;align-items:center;justify-content:center;min-height:48px;margin:1rem 0;padding:.7rem 1rem;border-radius:14px;background:linear-gradient(90deg,rgb(52,180,255),rgb(145,62,248));color:#fff;text-decoration:none;font-weight:800}
.at-related{display:grid;gap:.75rem;margin:1rem 0 1.5rem}
.at-related a{display:block;padding:.9rem 1rem;border-radius:16px;border:1px solid rgba(145,62,248,.28);text-decoration:none;color:inherit;background:linear-gradient(180deg,rgba(52,180,255,.08),rgba(145,62,248,.06))}
.at-related strong{display:block}
table{width:100%;border-collapse:collapse;display:block;overflow-x:auto}
th,td{text-align:left;padding:.55rem .4rem;border-bottom:1px solid #e3e8ef;vertical-align:top}
pre{overflow-x:auto;padding:1rem;border-radius:12px;background:#1e1e1e;color:#d4d4d4}
@media (min-width:720px){.at-page{padding:1.5rem 1.25rem 3rem}.at-page h1{font-size:2.15rem}}
`;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function headingOf(blocks) {
  const heading = blocks.find((block) => block.type === "h1");
  if (!heading) throw new Error("Article is missing an h1");
  return heading.text;
}

function localeById(id) {
  const locale = LOCALES.find((item) => item.id === id);
  if (!locale) throw new Error("Unknown locale " + id);
  return locale;
}

function siblingArticle(articleId) {
  return ARTICLES.find((item) => item.id !== articleId);
}

function routeFor(article, localeId) {
  const locale = localeById(localeId);
  const copy = article.copy[localeId];
  if (!copy) throw new Error(article.id + " is missing " + localeId);
  const path = article.paths[localeId];
  return {
    articleId: article.id,
    localeId,
    path,
    canonical: ORIGIN + path,
    htmlLang: locale.htmlLang,
    hreflang: locale.hreflang,
    ogLocale: locale.ogLocale,
    label: locale.label,
    title: copy.title,
    description: copy.description,
    heading: headingOf(copy.blocks),
    blocks: copy.blocks,
    ui: UI[localeId],
    hero: article.hero || null,
  };
}

function relatedFor(article, localeId) {
  const sibling = siblingArticle(article.id);
  return (article.related || [])
    .filter((id) => id !== sibling.id)
    .map((id) => {
      const related = ARTICLES.find((item) => item.id === id);
      if (!related) return null;
      const route = routeFor(related, localeId);
      return {
        path: route.path,
        heading: route.heading,
        title: route.title,
        hero: related.hero || null,
      };
    })
    .filter(Boolean);
}

function alternatesFor(article) {
  const links = LOCALES.map((locale) => {
    const route = routeFor(article, locale.id);
    return { hreflang: locale.hreflang, href: route.canonical, path: route.path, label: locale.label };
  });
  links.push({
    hreflang: "x-default",
    href: ORIGIN + article.paths.en,
    path: article.paths.en,
    label: "English",
  });
  return links;
}

function allRoutes() {
  return ARTICLES.flatMap((article) => LOCALES.map((locale) => {
    const route = routeFor(article, locale.id);
    const sibling = routeFor(siblingArticle(article.id), locale.id);
    return {
      ...route,
      alternates: alternatesFor(article),
      sibling: { path: sibling.path, heading: sibling.heading, title: sibling.title },
      related: relatedFor(article, locale.id),
    };
  }));
}

function routeByPath(pathname) {
  return allRoutes().find((route) => route.path === pathname) || null;
}

function renderBlocksHtml(blocks, imageSources, ui) {
  return blocks.map((block) => {
    if (block.type === "kicker") return `<p class="at-kicker">${escapeHtml(block.text)}</p>`;
    if (block.type === "h1") return `<h1>${escapeHtml(block.text)}</h1>`;
    if (block.type === "h2") return `<h2>${escapeHtml(block.text)}</h2>`;
    if (block.type === "p") return `<p>${escapeHtml(block.text)}</p>`;
    if (block.type === "note") return `<p class="at-note">${escapeHtml(block.text)}</p>`;
    if (block.type === "ul" || block.type === "ol") {
      const tag = block.type;
      const items = block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
      return `<${tag}>${items}</${tag}>`;
    }
    if (block.type === "image") {
      const src = imageSources[block.stem] ? ` src="${escapeHtml(imageSources[block.stem])}"` : "";
      const caption = block.caption ? `<figcaption>${escapeHtml(block.caption)}</figcaption>` : "";
      return `<figure><img${src} alt="${escapeHtml(block.alt)}" />${caption}</figure>`;
    }
    if (block.type === "callout") {
      const tone = block.tone && block.tone !== "info" ? ` ${escapeHtml(block.tone)}` : "";
      const items = (block.items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join("");
      const text = block.text ? `<p>${escapeHtml(block.text)}</p>` : "";
      return `<aside class="at-callout${tone}"><h2>${escapeHtml(block.title)}</h2>${text}${items ? `<ul>${items}</ul>` : ""}</aside>`;
    }
    if (block.type === "table") {
      const rows = block.rows.map((row) => `<tr><th>${escapeHtml(row[0])}</th><td>${escapeHtml(row[1])}</td></tr>`).join("");
      return `<table><tbody>${rows}</tbody></table>`;
    }
    if (block.type === "pre") return `<pre>${escapeHtml(block.text)}</pre>`;
    if (block.type === "link") return `<p><a class="at-cta" href="${escapeHtml(block.href)}">${escapeHtml(block.label)}</a></p>`;
    if (block.type === "rich") {
      return `<p>${escapeHtml(block.before)}<a href="${escapeHtml(block.href)}">${escapeHtml(block.label)}</a>${escapeHtml(block.after)}</p>`;
    }
    if (block.type === "sources") {
      const items = (block.items || []).map((item) => (
        `<li><a href="${escapeHtml(item.href)}">${escapeHtml(item.label)}</a></li>`
      )).join("");
      return `<h2>${escapeHtml(ui.sources)}</h2><ul>${items}</ul>`;
    }
    return "";
  }).join("");
}

function renderRouteHtml(route, imageSources) {
  const languages = route.alternates
    .filter((item) => item.hreflang !== "x-default")
    .map((item) => `<a href="${escapeHtml(item.path)}" hrefLang="${escapeHtml(item.hreflang)}">${escapeHtml(item.label)}</a>`)
    .join(" ");
  const related = (route.related || []).length
    ? `<h2>${escapeHtml(route.ui.related)}</h2><div class="at-related">${route.related.map((item) => (
      `<a href="${escapeHtml(item.path)}"><strong>${escapeHtml(item.heading)}</strong></a>`
    )).join("")}</div>`
    : "";
  return `<style>${ARTICLE_CSS}</style>` +
    `<article class="at-page" lang="${escapeHtml(route.htmlLang)}" data-article-lang="${escapeHtml(route.localeId)}">` +
    `<a class="at-brand" href="/"><span>AviatorTrends</span></a>` +
    `<nav class="at-langs" aria-label="${escapeHtml(route.ui.languages)}">${languages}</nav>` +
    renderBlocksHtml(route.blocks, imageSources || {}, route.ui) +
    related +
    `<p>${escapeHtml(route.ui.readAlso)} <a href="${escapeHtml(route.sibling.path)}">${escapeHtml(route.sibling.heading)}</a></p>` +
    `<p><a class="at-cta" href="/">${escapeHtml(route.ui.home)}</a></p>` +
    `</article>`;
}

function headTags(route, imageSources) {
  const alternates = route.alternates.map((item) => (
    `<link rel="alternate" hrefLang="${escapeHtml(item.hreflang)}" href="${escapeHtml(item.href)}" />`
  )).join("");
  const heroSrc = imageSources && route.hero && imageSources[route.hero];
  const ogImage = heroSrc
    ? `<meta property="og:image" content="${escapeHtml(ORIGIN + heroSrc)}" />`
    : "";
  const jsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: route.heading,
    description: route.description,
    inLanguage: route.htmlLang,
    mainEntityOfPage: route.canonical,
    url: route.canonical,
    publisher: { "@type": "Organization", name: "AviatorTrends", url: ORIGIN },
  }).replace(/</g, "\\u003c");
  return [
    `<meta name="description" content="${escapeHtml(route.description)}" />`,
    `<link rel="canonical" href="${escapeHtml(route.canonical)}" />`,
    alternates,
    `<meta property="og:type" content="article" />`,
    `<meta property="og:title" content="${escapeHtml(route.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(route.description)}" />`,
    `<meta property="og:url" content="${escapeHtml(route.canonical)}" />`,
    `<meta property="og:locale" content="${escapeHtml(route.ogLocale)}" />`,
    ogImage,
    `<script type="application/ld+json">${jsonLd}</script>`,
  ].join("");
}

function buildSitemapXml() {
  const urls = allRoutes().map((route) => {
    const links = route.alternates.map((item) => (
      `    <xhtml:link rel="alternate" hreflang="${escapeHtml(item.hreflang)}" href="${escapeHtml(item.href)}" />`
    )).join("\n");
    return `  <url>\n    <loc>${escapeHtml(route.canonical)}</loc>\n${links}\n  </url>`;
  }).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n` +
    `${urls}\n</urlset>\n`;
}

module.exports = {
  ORIGIN,
  LOCALES,
  allRoutes,
  routeByPath,
  renderRouteHtml,
  headTags,
  buildSitemapXml,
  escapeHtml,
};
